<script setup>
import { computed, watch, onMounted } from 'vue'
import { useSavingsCalculations } from '../composables/useSavingsCalculations'

const props = defineProps({
  currentSavingsGoal: {
    type: Number,
    default: 0
  }
})

const emit = defineEmits(['averageSavedUpdated'])

const { averageDailySaved } = useSavingsCalculations()

const averageSaved = computed(() => {
  return averageDailySaved.value
})

const savingsPerformance = computed(() => {
  if (props.currentSavingsGoal === 0) return 100
  return Math.round((averageSaved.value / props.currentSavingsGoal) * 100)
})

watch(averageSaved, (newValue) => {
  emit('averageSavedUpdated', newValue)
})

onMounted(() => {
  emit('averageSavedUpdated', averageSaved.value)
})
</script>

<template>
  <div class="average-saved-card card savings-positive">

    <!-- Card Header -->
    <div class="card-header">
      <div class="header-icon">
        <i class="bi bi-piggy-bank"></i>
      </div>
      <div class="header-content">
        <h3 class="card-title">Average Daily Saved</h3>
        <p class="card-subtitle">Your saving habits</p>
      </div>
    </div>

    <!-- Card Body -->
    <div class="card-body">
      <div class="row mb-2">
        <div class="col">
          <label for="transactionDescription" class="form-label fw-semibold">
            <i class="fa-solid fa-dollar-sign me-1"></i>Typical Daily Savings
          </label>
        </div>
      </div>
      <div class="row">
        <div class="col text-center">
          <span class="amount-value">{{ averageSaved.toFixed(2) }}</span>
        </div>
      </div>
    </div>
  </div>
</template>

<style scoped>
.savings-negative .header-icon {
  background: linear-gradient(135deg, var(--light-danger), #ff6b6b);
}

.header-content {
  flex: 1;
  text-align: left;
}

/* Amount Section */
.amount-section {
  margin-bottom: 1.5rem;
}

.amount-container {
  display: flex;
  align-items: baseline;
  justify-content: center;
  gap: 0.25rem;
  margin-bottom: 0.75rem;
}

.currency-symbol {
  font-size: 1.5rem;
  font-weight: 600;
  color: var(--light-text-muted);
}

.amount-status {
  margin-top: 0.5rem;
}



/* Goal Section */
.goal-section {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 1.5rem;
  padding: 1rem;
  background: rgba(0, 0, 0, 0.02);
  border-radius: 12px;
}

.goal-item {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  flex: 1;
}

.goal-icon {
  width: 36px;
  height: 36px;
  border-radius: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 1rem;
  flex-shrink: 0;
}

.target-icon {
  background: rgba(255, 193, 7, 0.1);
  color: #ffc107;
}

.budget-icon {
  background: rgba(13, 110, 253, 0.1);
  color: #0d6efd;
}

.goal-content {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  min-width: 0;
}

.goal-label {
  font-size: 0.75rem;
  color: var(--light-text-muted);
  font-weight: 500;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  line-height: 1;
}

.goal-value {
  font-size: 1rem;
  font-weight: 700;
  color: var(--light-text);
  margin-top: 0.25rem;
  line-height: 1;
}

.goal-divider {
  width: 1px;
  height: 40px;
  background: var(--light-border);
  margin: 0 1rem;
  flex-shrink: 0;
}

/* Progress Section */
.progress-section {
  margin-top: 1rem;
}

.progress-bar-container {
  width: 100%;
  height: 8px;
  background: var(--light-border);
  border-radius: 4px;
  overflow: hidden;
  margin-bottom: 0.75rem;
}

.progress-bar {
  height: 100%;
  border-radius: 4px;
  transition: all 0.6s ease;
  background: var(--light-success);
}

.progress-labels {
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.progress-label {
  font-size: 0.8rem;
  color: var(--light-text-muted);
  font-weight: 500;
}

.progress-percentage {
  font-size: 0.8rem;
  font-weight: 700;
  color: var(--light-text);
}

/* Dark Mode Adjustments */
:global([data-theme]) .goal-section {
  background: rgba(255, 255, 255, 0.05);
}

:global([data-theme]) .progress-bar-container {
  background: var(--light-border);
}

/* Responsive Design */
@media (max-width: 768px) {
  .currency-symbol {
    font-size: 1.25rem;
  }

  .goal-section {
    flex-direction: column;
    gap: 1rem;
    padding: 0.75rem;
  }

  .goal-item {
    width: 100%;
    justify-content: center;
  }

  .goal-divider {
    width: 100%;
    height: 1px;
    margin: 0;
  }

  .goal-content {
    align-items: center;
    text-align: center;
  }
}
</style>